/**
 * @file Detail-form API helpers shared by parent-board detail views.
 */

import {
    arrayRemove as firestoreArrayRemove,
    arrayUnion as firestoreArrayUnion,
    doc as firestoreDoc,
    serverTimestamp as firestoreServerTimestamp,
    updateDoc as firestoreUpdateDoc,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { db as configuredDb } from "../../app/firebase/firebase.js";
import {
    appData as configuredAppData,
    appDataDetails as configuredAppDataDetails,
    state as configuredState,
} from "../../app/state/state.js";
import {
    renderDetailsView as configuredRenderDetailsView,
    updateSelectBackgrounds as configuredUpdateSelectBackgrounds,
} from "../../app/detail/detailController.js";
import { openConfirmationModal as configuredOpenConfirmationModal } from "../ui/confirmationModal.js";
import {
    getRegisteredControl,
    mergeRegisteredControlValues,
    validateRegisteredControls,
} from "../ui/controls/controlCore.js";
import {
    openParentClosePreflightModal as configuredOpenParentClosePreflightModal,
} from "../details/parentClosePreflight.js";
import {
    applyParentCascadingClosure as configuredApplyParentCascadingClosure,
    applyParentCascadingHold as configuredApplyParentCascadingHold,
    getParentClosePreflight as configuredGetParentClosePreflight,
    getParentHoldPreflight as configuredGetParentHoldPreflight,
} from "./parentClosePreflight.js";
import {
    findItem as configuredFindItem,
    getCollectionName as configuredGetCollectionName,
    getItemType as configuredGetItemType,
} from "../utils/itemLookup.js";
import {
    formatContentForSave as configuredFormatContentForSave,
} from "../utils/contentUtils.js";
import {
    resolveItemClosedFromStatus as configuredResolveItemClosedFromStatus,
} from "../utils/closedState.js";

const CURRENCY_FIELDS = ['case_value', 'purchase_price', 'budget', 'cost'];
const RAW_DETAIL_TEXT_FIELDS = {
    asset: new Set(['comments']),
    patient: new Set(['comments']),
    project: new Set(['comments']),
    task: new Set(['description']),
    lab: new Set(['details']),
    maintenance: new Set(['details']),
};

function shouldSaveRawDetailText(itemType, fieldName) {
    return RAW_DETAIL_TEXT_FIELDS[itemType]?.has(fieldName);
}

const configuredCleanCurrency = (value) => {
    if ((value === null || value === undefined) ||
        typeof value !== 'string' ||
        value.trim() === '') {
        return null;
    }
    const number = Number(String(value).replace(/[$,]/g, ''));
    return Number.isNaN(number) || !Number.isFinite(number) ? null : number;
};

const configuredShouldRequireRecurrenceAnchor = (frequency, modifier) => {
    if (frequency === "daily" || frequency === "yearly") {return true;}
    if (frequency === "weekly") {return modifier === "same_day";}
    if (frequency === "monthly") {return modifier === "same_date";}
    return false;
};

const configuredGetItemClosedFieldName = (itemType) =>
    itemType === 'asset' ? 'equipment_status' : 'status';

const configuredGetResolvedClosedUpdate = (itemType, statusId) => ({
    isClosedResolved: configuredResolveItemClosedFromStatus(itemType, statusId),
});

function applyLocalDetailUpdate(item, updateData) {
    if (!item || !updateData) {return;}

    Object.entries(updateData).forEach(([key, value]) => {
        if (key === 'updatedAt') {return;}
        item[key] = value;
    });
}

function focusFirstRegisteredControlError(validation) {
    const firstError = validation.errors[0];
    const handle = firstError ? getRegisteredControl(firstError.id) : null;
    const focusTarget = handle?.root?.querySelector?.(
        "button, [tabindex], input, select, textarea",
    );

    if (typeof focusTarget?.focus === "function") {
        focusTarget.focus({ preventScroll: true });
    }

    handle?.root?.scrollIntoView?.({
        block: "center",
        behavior: "smooth",
    });
}

/**
 * Merges active registered-control values over the legacy detail FormData payload.
 * @param {object} params Detail form extraction context.
 * @param {HTMLFormElement} params.form Active detail form.
 * @param {object} params.data Existing FormData-derived payload.
 * @returns {{data: object, valid: boolean}} Merged payload and validation status.
 */
function mergeDetailRegisteredControlData({ form, data }) {
    const validation = validateRegisteredControls(form);
    if (!validation.valid) {
        focusFirstRegisteredControlError(validation);
        return {
            data,
            valid: false,
        };
    }

    return {
        data: mergeRegisteredControlValues(data, form),
        valid: true,
    };
}

/**
 * Creates detail-form API helpers.
 * @param {object} deps Dependency bag.
 * @returns {object} Detail API helpers.
 */
export function createDetailsApi({
    appData,
    appDataDetails,
    applyParentCascadingClosure,
    applyParentCascadingHold,
    arrayRemove,
    arrayUnion,
    cleanCurrency,
    db,
    doc,
    findItem,
    formatContentForSave,
    getCollectionName,
    getItemClosedFieldName,
    getItemType,
    getParentClosePreflight,
    getParentHoldPreflight,
    getResolvedClosedUpdate,
    openConfirmationModal,
    openParentClosePreflight,
    renderDetailsView,
    serverTimestamp,
    shouldRequireRecurrenceAnchor,
    state,
    updateDoc,
    updateSelectBackgrounds,
}) {
    let isDetailFieldUpdating = false;

    const getDetailItemAndType = (target = {}) => {
        const itemId = target.itemId || state.selectedItemId;
        if (!itemId) {return { item: null, itemType: null };}

        let item = findItem(itemId);
        let itemType = target.itemType || getItemType(item?.id);

        if (!item) {
            item = appDataDetails.tasks.find((task) => task.id === itemId);
            if (item) {itemType = target.itemType || 'task';}
        }
        return { item, itemType };
    };

    /**
     * Handles live detail field updates.
     * @param {HTMLElement} element Changed input/select element.
     * @param {object} [target] Explicit target for queued live saves.
     * @param {string} [target.itemId] Item ID to update.
     * @param {string} [target.itemType] Item type to update.
     * @param {boolean} [target.suppressFailureRender] Whether the caller will
     * handle failed-save re-rendering after clearing optimistic state.
     * @returns {Promise<boolean>} Whether the save was accepted or completed.
     */
    const handleDetailFieldChange = async (element, target = {}) => {
        if (isDetailFieldUpdating) {return false;}

        isDetailFieldUpdating = true;
        const originalDisabledState = element.disabled;
        element.disabled = true;

        try {
            const { item, itemType } = getDetailItemAndType(target);
            const isCurrentDetailItem = item?.id === state.selectedItemId;

            if (!item || !itemType) {
                console.error("handleDetailFieldChange: No selected item or type found in state.");
                return false;
            }

            const { name, type, checked } = element;
            let { value } = element;

            if (CURRENCY_FIELDS.includes(name)) {
                value = cleanCurrency(value);
            }

            const updatedValue = (type === 'checkbox') ? checked : value;
            const updateData = {
                [name]: updatedValue,
                updatedBy: state.currentUser.uid,
                updatedAt: serverTimestamp(),
            };
            if (itemType === 'task') {
                updateData.isSubtask = Boolean(item.parentId);
            }

            if (
                (name === 'status' && ['task', 'patient', 'project'].includes(itemType)) ||
                (name === 'equipment_status' && itemType === 'asset')
            ) {
                Object.assign(updateData, getResolvedClosedUpdate(itemType, updatedValue));
            }

            const collectionName = getCollectionName(itemType);
            const docRef = doc(db, collectionName, item.id);

            if (name === 'status' || name === 'equipment_status') {
                const config = appData?.config || {};
                let statusList = [];
                if (itemType === 'patient') {statusList = config.patientStatus || [];}
                else if (itemType === 'project') {statusList = config.projectStatus || [];}
                else if (itemType === 'asset') {statusList = config.equipmentStatus || [];}

                const newStatusConfig = statusList.find((status) =>
                    status.id === updatedValue,
                );
                const isClosingStatus = newStatusConfig?.isClosed === true;
                const isOnHoldStatus = newStatusConfig?.isOnHold === true;

                if (isClosingStatus && isCurrentDetailItem) {
                    const preflight = await getParentClosePreflight({
                        itemId: item.id,
                        itemType,
                        targetStatusId: updatedValue,
                    });

                    if (preflight?.shouldIntercept) {
                        const finishBackendClose = async ({
                            cascade,
                            labCompletionDataById,
                            selections,
                        }) => {
                            try {
                                await applyParentCascadingClosure({
                                    cascade,
                                    itemId: item.id,
                                    itemType,
                                    labCompletionDataById,
                                    selections,
                                    targetStatusId: updatedValue,
                                });
                                applyLocalDetailUpdate(item, updateData);
                                updateSelectBackgrounds();
                            } catch (error) {
                                console.error(
                                    `Error applying parent close for ${item.id}:`,
                                    error,
                                );
                                throw error;
                            } finally {
                                isDetailFieldUpdating = false;
                                element.disabled = originalDisabledState;
                            }
                        };

                        openParentClosePreflight({
                            onCancel: () => {
                                renderDetailsView();
                                updateSelectBackgrounds();
                            },
                            preflight,
                            onApply: finishBackendClose,
                            onSkip: finishBackendClose,
                        });
                        return true;
                    }
                }

                if (isOnHoldStatus && !isClosingStatus) {
                    const preflight = await getParentHoldPreflight({
                        itemId: item.id,
                        itemType,
                        targetStatusId: updatedValue,
                    });

                    if (preflight?.shouldIntercept) {
                        const finishBackendHold = async ({
                            cascade,
                            selections,
                        }) => {
                            try {
                                await applyParentCascadingHold({
                                    cascade,
                                    itemId: item.id,
                                    itemType,
                                    selections,
                                    targetStatusId: updatedValue,
                                });
                                applyLocalDetailUpdate(item, updateData);
                                updateSelectBackgrounds();
                            } catch (error) {
                                console.error(
                                    `Error applying on-hold cascade for ${item.id}:`,
                                    error,
                                );
                                throw error;
                            } finally {
                                isDetailFieldUpdating = false;
                                element.disabled = originalDisabledState;
                            }
                        };

                        openParentClosePreflight({
                            onCancel: () => {
                                renderDetailsView();
                                updateSelectBackgrounds();
                            },
                            preflight,
                            onApply: finishBackendHold,
                            onSkip: finishBackendHold,
                        });
                        return true;
                    }
                }
            }

            await updateDoc(docRef, updateData);
            applyLocalDetailUpdate(item, updateData);
            if (isCurrentDetailItem) {
                renderDetailsView();
                updateSelectBackgrounds();
            }
            return true;
        } catch (error) {
            console.error(`Error updating document:`, error);
            const isCurrentDetailItem = (
                target.itemId || state.selectedItemId
            ) === state.selectedItemId;
            if (isCurrentDetailItem && target.suppressFailureRender !== true) {
                renderDetailsView();
                updateSelectBackgrounds();
            }
            const errorMessage = (error.code === 'internal' || !error.message)
                ? 'Something went wrong while saving this field. Please try again.'
                : `Failed to save field: ${error.message}`;
            openConfirmationModal('Error', errorMessage, () => { }, false, 'OK');
            return false;
        } finally {
            isDetailFieldUpdating = false;
            element.disabled = originalDisabledState;
        }
    };

    /**
     * Saves a raw Details text field without mention conversion or full re-render.
     * @param {string} fieldName Details field name.
     * @param {string} value Raw text value to save.
     * @returns {Promise<boolean>} Whether the field was saved.
     */
    const saveDetailTextField = async (fieldName, value) => {
        const { item, itemType } = getDetailItemAndType();

        if (!item || !itemType || !shouldSaveRawDetailText(itemType, fieldName)) {
            console.error("saveDetailTextField: Invalid detail text target.");
            return false;
        }

        const updateData = {
            [fieldName]: value,
            updatedBy: state.currentUser.uid,
            updatedAt: serverTimestamp(),
        };
        if (itemType === 'task') {
            updateData.isSubtask = Boolean(item.parentId);
        }

        const collectionName = getCollectionName(itemType);
        const docRef = doc(db, collectionName, item.id);

        try {
            await updateDoc(docRef, updateData);
            applyLocalDetailUpdate(item, updateData);
            return true;
        } catch (error) {
            console.error("Error saving detail text field:", error);
            const errorMessage = (error.code === 'internal' || !error.message)
                ? 'Something went wrong. Please try again later.'
                : `Failed to save Details: ${error.message}`;
            openConfirmationModal('Error', errorMessage, async () => { }, false, 'OK');
            throw error;
        }
    };

    /**
     * Handles multi-select array field changes.
     * @param {string} action Add/remove action.
     * @param {string} field Field name.
     * @param {string} value Value to add/remove.
     * @returns {Promise<void>}
     */
    const handleMultiSelectChange = async (action, field, value) => {
        const { item, itemType } = getDetailItemAndType();

        if (!item || !itemType) {
            console.error("handleMultiSelectChange: No item/type found.");
            return;
        }

        const collectionName = getCollectionName(itemType);
        const docRef = doc(db, collectionName, item.id);

        try {
            const updateData = {
                [field]: action === 'add' ? arrayUnion(value) : arrayRemove(value),
                updatedBy: state.currentUser.uid,
                updatedAt: serverTimestamp(),
            };
            if (itemType === 'task') {
                updateData.isSubtask = Boolean(item.parentId);
            }
            await updateDoc(docRef, updateData);
            renderDetailsView();
        } catch (error) {
            console.error(`Error updating multi-select field '${field}':`, error);
        }
    };

    /**
     * Saves the whole detail form manually.
     * @param {HTMLFormElement} formElement Details form element.
     * @returns {Promise<boolean>} Whether the save completed successfully.
     */
    const saveDetailsForm = async (formElement) => {
        const itemId = state.selectedItemId;
        let item = findItem(itemId);
        let itemType = getItemType(item?.id);
        if (!item && state.selectedItemId) {
            item = appDataDetails.tasks.find((task) => task.id === state.selectedItemId);
            if (item) {itemType = 'task';}
        }

        if (!itemId || !itemType) {
            console.error("saveDetailsForm: No selected item found.");
            return false;
        }

        const formData = new FormData(formElement);
        const legacyUpdateData = {};

        for (const [key, value] of formData.entries()) {
            if (typeof value === 'string') {
                if (key.includes('form-') ||
                    key === 'undefined' ||
                    key === 'types_validator') {
                    continue;
                }
                legacyUpdateData[key] = shouldSaveRawDetailText(itemType, key) ?
                    value :
                    formatContentForSave(value);
            } else {
                legacyUpdateData[key] = value;
            }
        }

        const mergedPayload = mergeDetailRegisteredControlData({
            form: formElement,
            data: legacyUpdateData,
        });
        if (!mergedPayload.valid) {return false;}

        const updateData = mergedPayload.data;

        CURRENCY_FIELDS.forEach((field) => {
            if (updateData[field]) {updateData[field] = cleanCurrency(updateData[field]);}
        });

        Array.from(formElement.elements).forEach((element) => {
            if (element.type === 'checkbox') {
                updateData[element.name] = element.checked;
            }
        });

        const statusFieldName = getItemClosedFieldName(itemType);
        if (Object.prototype.hasOwnProperty.call(updateData, statusFieldName)) {
            Object.assign(
                updateData,
                getResolvedClosedUpdate(itemType, updateData[statusFieldName]),
            );
        }

        const hasTaskRecurrenceFields = itemType === 'task' && (
            Object.prototype.hasOwnProperty.call(updateData, 'recurrence_enabled') ||
            Object.prototype.hasOwnProperty.call(updateData, 'recurrence_frequency') ||
            Object.prototype.hasOwnProperty.call(updateData, 'recurrence_modifier') ||
            Object.prototype.hasOwnProperty.call(updateData, 'recurrence_anchor')
        );

        if (hasTaskRecurrenceFields) {
            const recurrenceEnabled = updateData.recurrence_enabled === 'true';
            const recurrenceFrequency = updateData.recurrence_frequency || null;
            const recurrenceAnchor = updateData.recurrence_anchor || null;
            let recurrenceModifier = updateData.recurrence_modifier || null;

            delete updateData.recurrence_enabled;
            delete updateData.recurrence_frequency;
            delete updateData.recurrence_modifier;
            delete updateData.recurrence_anchor;

            if (!recurrenceEnabled) {
                updateData.recurrence = {
                    frequency: null,
                    modifier: null,
                    anchor: null,
                };
            } else {
                if (!updateData.due_date && !item?.due_date) {
                    openConfirmationModal(
                        'Due Date Required',
                        'Recurring tasks require a due date.',
                        async () => { },
                        false,
                        'OK',
                    );
                    return false;
                }

                if (!recurrenceFrequency) {
                    openConfirmationModal(
                        'Repeat Rule Required',
                        'Choose how often this task repeats.',
                        async () => { },
                        false,
                        'OK',
                    );
                    return false;
                }

                if (shouldRequireRecurrenceAnchor(
                    recurrenceFrequency,
                    recurrenceModifier,
                ) && !recurrenceAnchor) {
                    openConfirmationModal(
                        'Calculation Rule Required',
                        'Choose what the recurrence should be calculated from.',
                        async () => { },
                        false,
                        'OK',
                    );
                    return false;
                }

                if ((recurrenceFrequency === 'weekly' ||
                    recurrenceFrequency === 'monthly') &&
                    !recurrenceModifier) {
                    openConfirmationModal(
                        'Repeat Rule Required',
                        'Choose the recurrence rule for this task.',
                        async () => { },
                        false,
                        'OK',
                    );
                    return false;
                }

                if (recurrenceFrequency !== 'weekly' &&
                    recurrenceFrequency !== 'monthly') {
                    recurrenceModifier = null;
                }

                updateData.recurrence = {
                    frequency: recurrenceFrequency,
                    modifier: recurrenceModifier,
                    anchor: shouldRequireRecurrenceAnchor(
                        recurrenceFrequency,
                        recurrenceModifier,
                    ) ? recurrenceAnchor : null,
                };
            }
        }

        updateData.updatedBy = state.currentUser.uid;
        updateData.updatedAt = serverTimestamp();
        if (itemType === 'task') {
            updateData.isSubtask = Boolean(item.parentId);
        }

        const collectionName = getCollectionName(itemType);
        const docRef = doc(db, collectionName, itemId);

        try {
            await updateDoc(docRef, updateData);
            state.hasUnsavedChanges = false;
            return true;
        } catch (error) {
            console.error("Error saving details form:", error);
            const errorMessage = (error.code === 'internal' || !error.message)
                ? 'Something went wrong. Please try again later.'
                : `Failed to save: ${error.message}`;
            openConfirmationModal('Error', errorMessage, async () => { }, false, 'OK');
            throw error;
        }
    };

    return {
        handleDetailFieldChange,
        handleMultiSelectChange,
        saveDetailTextField,
        saveDetailsForm,
    };
}

const configuredDetailsApi = createDetailsApi({
    appData: configuredAppData,
    appDataDetails: configuredAppDataDetails,
    applyParentCascadingClosure: configuredApplyParentCascadingClosure,
    applyParentCascadingHold: configuredApplyParentCascadingHold,
    arrayRemove: firestoreArrayRemove,
    arrayUnion: firestoreArrayUnion,
    cleanCurrency: configuredCleanCurrency,
    db: configuredDb,
    doc: firestoreDoc,
    findItem: configuredFindItem,
    formatContentForSave: configuredFormatContentForSave,
    getCollectionName: configuredGetCollectionName,
    getItemClosedFieldName: configuredGetItemClosedFieldName,
    getItemType: configuredGetItemType,
    getParentClosePreflight: configuredGetParentClosePreflight,
    getParentHoldPreflight: configuredGetParentHoldPreflight,
    getResolvedClosedUpdate: configuredGetResolvedClosedUpdate,
    openConfirmationModal: configuredOpenConfirmationModal,
    openParentClosePreflight: configuredOpenParentClosePreflightModal,
    renderDetailsView: configuredRenderDetailsView,
    serverTimestamp: firestoreServerTimestamp,
    shouldRequireRecurrenceAnchor: configuredShouldRequireRecurrenceAnchor,
    state: configuredState,
    updateDoc: firestoreUpdateDoc,
    updateSelectBackgrounds: configuredUpdateSelectBackgrounds,
});

export const handleDetailFieldChange =
    configuredDetailsApi.handleDetailFieldChange;
export const handleMultiSelectChange =
    configuredDetailsApi.handleMultiSelectChange;
export const saveDetailTextField = configuredDetailsApi.saveDetailTextField;
export const saveDetailsForm = configuredDetailsApi.saveDetailsForm;
